import { inject, Injector } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { resetBodyUiLocks } from '../utils/ui-lock.util';
import { resolveAcceptLanguage } from '../utils/locale.util';
import { environment } from '../../../environments/environment';

const AUTH_EXEMPT_PATHS = [
  '/v1/auth/login',
  '/v1/auth/register',
  '/v1/auth/check-email',
  '/v1/auth/password/email',
  '/v1/auth/password/reset',
  '/v1/auth/email/verify',
  '/v1/auth/google/',
];

/** Optional endpoints: a 401 must not wipe the whole session (e.g. right after CLIENT login). */
const SOFT_401_PATHS = [
  '/v1/notifications',
];

const isAuthExempt = (url: string): boolean =>
  AUTH_EXEMPT_PATHS.some((path) => url.includes(path));

const isSoft401 = (url: string): boolean =>
  SOFT_401_PATHS.some((path) => url.includes(path));

const isApiRequest = (url: string): boolean =>
  url.startsWith(environment.apiUrl);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const router = inject(Router);
  const translate = inject(TranslateService, { optional: true });

  let authReq = req;

  if (isApiRequest(req.url)) {
    const headers: Record<string, string> = {
      'Accept-Language': resolveAcceptLanguage(translate?.currentLang || translate?.defaultLang),
    };

    const token = localStorage.getItem('token');
    if (token && !isAuthExempt(req.url)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    authReq = req.clone({ setHeaders: headers });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401
        && !isAuthExempt(req.url)
        && !isSoft401(req.url)
        && !router.url.startsWith('/auth/')
      ) {
        const authService = injector.get(AuthService);
        const returnUrl = router.url.startsWith('/access-denied') ? undefined : router.url;

        authService.signOut();
        resetBodyUiLocks();

        if (returnUrl) {
          router.navigate(['/auth/login'], { queryParams: { returnUrl } });
        } else {
          router.navigate(['/auth/login']);
        }
      }

      return throwError(() => error);
    })
  );
};
